// Lógica del formulario "Editar ficha de la empresa" (detalle de /admin/contracts):
// valores iniciales, validación y el payload de PATCH contracts/companies/:id/profile.
//
// La autoridad es microservice-contracts (revalida todo y decide quién cambia el RUT);
// esto es feedback inmediato y evita mandar lo que no cambió. En particular el RUT solo
// viaja si cambió: soporte y supervisor no pueden cambiarlo, y mandarlo igual les
// costaría un 403 al guardar solo un contacto.
import { isEmail, isValidTaxId } from "../../settings/sections/invoicing/fiscalValidation";

const CONTACT_FIELDS = ["name", "email", "phone"];

// Los mensajes del identificador se traducen con `{ taxIdLabel }` (RUT o CUIT): la misma
// clave que el wizard de alta.
const TAX_ID_INVALID = "{{taxIdLabel}} inválido (dígito verificador).";

/**
 * País del identificador según `company.operationsCountry` ("CHILE", "ARGENTINA" o, en
 * documentos viejos, código o vacío). Mismo criterio que contracts: vacío = Chile.
 * Devuelve null si no hay regla para el país.
 */
export function taxIdCountryOf(operationsCountry) {
  const value = String(operationsCountry ?? "").trim().toUpperCase();
  if (value === "" || value === "CL" || value === "CHILE") return "CL";
  if (value === "AR" || value === "ARGENTINA") return "AR";
  return null;
}

const text = (value) => (typeof value === "string" ? value : "");

const contactDefaults = (contact) => ({
  name: text(contact?.name),
  email: text(contact?.email),
  phone: text(contact?.phone),
});

/** Valores iniciales desde el contrato poblado (`contract.companyId`). */
export function profileDefaults(contract) {
  const company = contract?.companyId || {};
  const contacts = company.contacts || {};
  return {
    legalName: text(company.legalName),
    businessIdentifier: text(company.businessIdentifier),
    fiscalAddress: text(company.fiscalAddress),
    primary: contactDefaults(contacts.primary),
    billing: { ...contactDefaults(contacts.billing), rut: text(contacts.billing?.rut) },
  };
}

const changed = (a, b) => a.trim() !== b.trim();
const contactChanged = (values, initial, fields) =>
  fields.some((field) => changed(values[field], initial[field]));

/**
 * Errores por campo (clave i18n en español, o undefined). Claves con punto para los
 * contactos: "primary.email", "billing.rut".
 */
export function validateProfileForm(values, initial, { taxIdCountry }) {
  const errors = {};
  if (changed(values.legalName, initial.legalName) && !values.legalName.trim()) {
    errors.legalName = "La razón social es requerida";
  }
  if (changed(values.fiscalAddress, initial.fiscalAddress) && !values.fiscalAddress.trim()) {
    errors.fiscalAddress = "La dirección fiscal es requerida";
  }
  if (changed(values.businessIdentifier, initial.businessIdentifier)) {
    if (!values.businessIdentifier.trim()) {
      errors.businessIdentifier = "El {{taxIdLabel}} es requerido";
    } else if (!taxIdCountry || !isValidTaxId(taxIdCountry, values.businessIdentifier)) {
      errors.businessIdentifier = TAX_ID_INVALID;
    }
  }

  const groups = [
    ["primary", CONTACT_FIELDS],
    ["billing", [...CONTACT_FIELDS, "rut"]],
  ];
  for (const [which, fields] of groups) {
    const contact = values[which];
    if (!contactChanged(contact, initial[which], fields)) continue;
    // Un contacto editado se guarda completo: nombre, email y teléfono.
    if (!contact.name.trim()) errors[`${which}.name`] = "El nombre es requerido";
    if (!contact.email.trim()) errors[`${which}.email`] = "El email es requerido";
    else if (!isEmail(contact.email)) errors[`${which}.email`] = "Email inválido";
    if (!contact.phone.trim()) errors[`${which}.phone`] = "El teléfono es requerido";
    else if (!/^[0-9+()\s.-]+$/.test(contact.phone.trim())) errors[`${which}.phone`] = "Teléfono inválido";
  }
  const rut = values.billing.rut.trim();
  if (changed(values.billing.rut, initial.billing.rut) && rut && (!taxIdCountry || !isValidTaxId(taxIdCountry, rut))) {
    errors["billing.rut"] = TAX_ID_INVALID;
  }
  return errors;
}

/** Body del PATCH con solo lo que cambió; null si no cambió nada. */
export function buildProfilePayload(values, initial) {
  const payload = {};
  for (const field of ["legalName", "fiscalAddress", "businessIdentifier"]) {
    if (changed(values[field], initial[field])) payload[field] = values[field].trim();
  }
  const contacts = {};
  if (contactChanged(values.primary, initial.primary, CONTACT_FIELDS)) {
    contacts.primary = trimmedContact(values.primary);
  }
  if (contactChanged(values.billing, initial.billing, [...CONTACT_FIELDS, "rut"])) {
    contacts.billing = trimmedContact(values.billing);
    // Solo si cambió: vacío lo quita, ausente lo conserva.
    if (changed(values.billing.rut, initial.billing.rut)) contacts.billing.rut = values.billing.rut.trim();
  }
  if (Object.keys(contacts).length > 0) payload.contacts = contacts;
  return Object.keys(payload).length > 0 ? payload : null;
}

const trimmedContact = (contact) => ({
  name: contact.name.trim(),
  email: contact.email.trim(),
  phone: contact.phone.trim(),
});

// Mensaje (clave i18n) por `code` del contrato de error de contracts.
const ERROR_KEY_BY_CODE = {
  tax_id_admin_only: "Solo un administrador del operador puede cambiar el {{taxIdLabel}}.",
  tax_id_holding_child: "El {{taxIdLabel}} de una empresa de un holding lo administra el holding.",
  tax_id_taken: "Otra empresa ya tiene ese {{taxIdLabel}}.",
  tax_id_invalid: TAX_ID_INVALID,
  billing_rut_invalid: TAX_ID_INVALID,
  tax_id_country_unsupported: "No hay validación de identificador tributario para el país de esta empresa.",
  empty_field: "Hay campos obligatorios vacíos.",
  no_fields: "No hay cambios para guardar.",
};

/**
 * Clave i18n del error devuelto por updateCompanyProfile ({ error, status }).
 * 404 = la empresa ya no tiene contrato vigente con el operador (o no existe).
 */
export function profileErrorKey(result) {
  const code = result?.error?.code;
  if (code && ERROR_KEY_BY_CODE[code]) return ERROR_KEY_BY_CODE[code];
  if (result?.status === 404) return "La empresa no tiene un contrato vigente con tu operador.";
  if (result?.status === 400) return "Revisa los datos: hay campos con formato inválido.";
  if (result?.status === null) return "No se pudo guardar por un problema de conexión. Vuelve a intentarlo.";
  return "No se pudo guardar la ficha de la empresa.";
}
